import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { APIResponse } from "src/app/core/models/api-response";
import { environment } from "src/environments/environment";
import { HttpClient } from "@angular/common/http";
import { IPageCount } from "src/app/shared/models/page.model";
import { IAddRole, ICustomField, ICustomFieldForm, IModule, INotification, INotificationTypeUpdate, IObjectCustomFields, IObjectCustomFieldsParams, IRole, IUser, IMailboxSettings, IMailboxSettingsUpdate, IOrdersSettings, IUserCreate, IUpdateRole, ISmartRepliesSettings, ISplittingRuleCreate, IAddTeam, ITeam, ITeamSort, ITeamOption, IUpdateTeam, TEAM_SORT_FIELDS } from "../models/settings.models";
import { IFieldVisibilityCatalog, HiddenFieldsByModule, FieldVisibilityModule } from "src/app/core/store/field-visibility/field-visibility.types";

@Injectable({
  providedIn: 'root',
})
export class SettingsService {

  constructor(private http: HttpClient) { }

  // Get Me
  public getMe(): Observable<APIResponse<IUser>> {
    return this.http.get<APIResponse<IUser>>(environment.BASE_URL_API + 'users/me');
  }

  // Update Me
  public updateMe(user: IUser): Observable<APIResponse<IUser>> {
    return this.http.patch<APIResponse<IUser>>(environment.BASE_URL_API + 'users/me', user);
  }

  // Users
  public listAllUsers(
    enabled: 'true' | 'false' | 'all' = 'all',
    deleted: 'true' | 'false' | 'all' = 'false',
  ): Observable<APIResponse<IUser>> {
    return this.http.get<APIResponse<IUser>>(
      `${environment.BASE_URL_API}users?enabled=${enabled}&deleted=${deleted}`
    );
  }

  public listUsers(
    page: IPageCount,
    enabled: 'true' | 'false' | 'all' = 'all',
    deleted: 'true' | 'false' | 'all' = 'false',
    search?: string,
  ): Observable<APIResponse<IUser>> {
    const q = (search ?? '').trim();
    const qParam = q ? `&q=${encodeURIComponent(q)}` : '';
    return this.http.get<APIResponse<IUser>>(
      `${environment.BASE_URL_API}users?page=${page.number}&limit=${page.size}&enabled=${enabled}&deleted=${deleted}${qParam}`
    );
  }

  public addUser(user: IUserCreate): Observable<APIResponse<IUser>> {
    return this.http.post<APIResponse<IUser>>(
      environment.BASE_URL_API + 'users',
      user
    );
  }

  public updateUser(id: number, user: Partial<IUser>): Observable<APIResponse<IUser>> {
    return this.http.patch<APIResponse<IUser>>(
      environment.BASE_URL_API + 'users/' + id,
      user
    );
  }

  public deleteUser(id: number): Observable<APIResponse<IUser>> {
    return this.http.delete<APIResponse<IUser>>(
      environment.BASE_URL_API + 'users/' + id,
    );
  }

  public restoreUser(id: number): Observable<APIResponse<IUser>> {
    return this.http.post<APIResponse<IUser>>(
      `${environment.BASE_URL_API}users/${id}/restore`,
      {}
    );
  }



  // Roles
  public listAllRoles(): Observable<APIResponse<IRole>> {
    return this.http.get<APIResponse<IRole>>(environment.BASE_URL_API + 'roles');
  }

  public listRoles(page: IPageCount): Observable<APIResponse<IRole>> {
    return this.http.get<APIResponse<IRole>>(environment.BASE_URL_API + 'roles' + '?page=' + page.number + '&limit=' + page.size);
  }

  public addRole(role: IAddRole): Observable<APIResponse<IRole>> {
    return this.http.post<APIResponse<IRole>>(
      environment.BASE_URL_API + 'roles',
      role
    );
  }

  setLanguage(lang: string): Observable<APIResponse<any>> {
    return this.http.patch<APIResponse<any>>(
      `${environment.BASE_URL_API}users/set-language`,
      { lang }
    );
  }
  public updateRole(id: number, role: IUpdateRole): Observable<APIResponse<IRole>> {
    return this.http.patch<APIResponse<IRole>>(
      environment.BASE_URL_API + 'roles/' + id,
      role
    );
  }

  public deleteRole(id: number): Observable<APIResponse<IRole>> {
    return this.http.delete<APIResponse<IRole>>(
      environment.BASE_URL_API + 'roles/' + id,
    );
  }

  // Teams
  /**
   * Sorting is server-side. `sort_mode` is case-sensitive on the wire — `ASC` returns 400 —
   * and only the three columns in `TEAM_SORT_FIELDS` are accepted; `write_date` is rejected
   * on purpose. Anything outside that vocabulary is dropped so the page falls back to the
   * server default (`name asc`) instead of failing the whole read with a 400.
   */
  private teamSortQuery(sort?: ITeamSort): string {
    if (!sort || !TEAM_SORT_FIELDS.includes(sort.sort_by)) return '';
    return '&sort_by=' + sort.sort_by + '&sort_mode=' + String(sort.sort_mode).toLowerCase();
  }

  public listAllTeams(sort?: ITeamSort): Observable<APIResponse<ITeam>> {
    const query = this.teamSortQuery(sort);
    return this.http.get<APIResponse<ITeam>>(
      environment.BASE_URL_API + 'teams' + (query ? '?' + query.slice(1) : '')
    );
  }

  /**
   * The team list for filter pickers. A separate route from `listAllTeams` on purpose:
   * `GET /v1/teams` is 403 without `access:settings_team`, and the Orders / Inbox filter
   * panels are used by ordinary users. `teams/options` answers to any of order, smart
   * inbox or settings_team and returns `{ id, name }` only.
   */
  public listTeamOptions(): Observable<APIResponse<ITeamOption>> {
    return this.http.get<APIResponse<ITeamOption>>(
      environment.BASE_URL_API + 'teams/options'
    );
  }

  public addTeam(team: IAddTeam): Observable<APIResponse<ITeam>> {
    return this.http.post<APIResponse<ITeam>>(
      environment.BASE_URL_API + 'teams',
      team
    );
  }

  public updateTeam(id: number, team: IUpdateTeam): Observable<APIResponse<ITeam>> {
    return this.http.patch<APIResponse<ITeam>>(
      environment.BASE_URL_API + 'teams/' + id,
      team
    );
  }

  public deleteTeam(id: number): Observable<APIResponse<ITeam>> {
    return this.http.delete<APIResponse<ITeam>>(
      environment.BASE_URL_API + 'teams/' + id,
    );
  }

  // Roles
  public listAllModules(): Observable<APIResponse<IModule>> {
    return this.http.get<APIResponse<IModule>>(environment.BASE_URL_API + 'modules');
  }

  // get notificatoins
  public listNotificationTypes(): Observable<APIResponse<INotification>> {
    return this.http.get<APIResponse<INotification>>(environment.BASE_URL_API + 'notifications');
  }

  // update notification type
  public updateNotifications(id: number, notificationType: INotificationTypeUpdate): Observable<APIResponse<INotificationTypeUpdate>> {
    return this.http.patch<APIResponse<INotificationTypeUpdate>>(
      environment.BASE_URL_API + 'notifications/' + id,
      notificationType
    );
  }

  // Custom Fields
  public listAllCustomFields(): Observable<APIResponse<ICustomField>> {
    return this.http.get<APIResponse<ICustomField>>(environment.BASE_URL_API + 'custom-fields');
  }

  public listCustomFields(page?: IPageCount, object_type?: string): Observable<APIResponse<ICustomField>> {
    return this.http.get<APIResponse<ICustomField>>(
      environment.BASE_URL_API + 'custom-fields' + '?page=' + page?.number + '&limit=' + page?.size + '&object_type=' + object_type
    );
  }

  public addCustomField(customField: ICustomFieldForm): Observable<APIResponse<ICustomField>> {
    return this.http.post<APIResponse<ICustomField>>(
      environment.BASE_URL_API + 'custom-fields',
      customField
    );
  }
  
  public updateCustomField(id: number, customField: ICustomFieldForm): Observable<APIResponse<ICustomField>> {
    return this.http.put<APIResponse<ICustomField>>(
      environment.BASE_URL_API + 'custom-fields/' + id,
      customField
    );
  }

  public deleteCustomField(id: number): Observable<APIResponse<ICustomField>> {
    return this.http.delete<APIResponse<ICustomField>>(
      environment.BASE_URL_API + 'custom-fields/' + id,
    );
  }

  // Get Object Custom Fields
  public getObjectCustomFields(params: IObjectCustomFieldsParams): Observable<APIResponse<IObjectCustomFields>> {
    return this.http.get<APIResponse<IObjectCustomFields>>(environment.BASE_URL_API + 'custom-fields/values/' + params.object_type + '/' + params.record_id + '?visibility=' + params.visibility?.join(',') + '&include_definitions=' + params.include_definitions?.toString() || 'false');
  }

  // Update Object Custom Fields values (keyed by technical_name)
  public updateObjectCustomFields(params: IObjectCustomFieldsParams, values: IObjectCustomFields['values']): Observable<APIResponse<IObjectCustomFields>> {
    return this.http.put<APIResponse<IObjectCustomFields>>(
      environment.BASE_URL_API + 'custom-fields/values/' + params.object_type + '/' + params.record_id,
      { values }
    );
  }

  // Smart Inbox settings (mailboxes) - /v1/inbox/settings/
  public listInboxMailboxes(params?: { limit?: number; page?: number }): Observable<APIResponse<IMailboxSettings>> {
    const limit = params?.limit ?? 20;
    const page = params?.page ?? 1;
    return this.http.get<APIResponse<IMailboxSettings>>(
      environment.BASE_URL_API + 'inbox/settings/mailboxes?limit=' + limit + '&page=' + page
    );
  }

  public getInboxMailbox(id: number): Observable<APIResponse<IMailboxSettings>> {
    return this.http.get<APIResponse<IMailboxSettings>>(
      environment.BASE_URL_API + 'inbox/settings/mailboxes/' + id
    );
  }

  public updateInboxMailbox(id: number, payload: IMailboxSettingsUpdate): Observable<APIResponse<IMailboxSettings>> {
    return this.http.patch<APIResponse<IMailboxSettings>>(
      environment.BASE_URL_API + 'inbox/settings/mailboxes/' + id,
      payload
    );
  }

  /** Reversible: keeps every conversation, mailbox stays listed as `disconnected`. Idempotent. */
  public disconnectInboxMailbox(id: number): Observable<APIResponse<{ success: boolean; message?: string }>> {
    return this.http.post<APIResponse<{ success: boolean; message?: string }>>(
      environment.BASE_URL_API + 'inbox/settings/mailboxes/' + id + '/disconnect',
      {}
    );
  }

  /**
   * PERMANENT: cascades every conversation. Only for the "Delete connection" branch.
   *
   * Settings-scoped rather than the owner-scoped `inbox/mailboxes/{id}`, because an admin
   * on this page usually does not own the connection they are removing.
   */
  public deleteInboxMailbox(id: number): Observable<APIResponse<{ success: boolean; message?: string }>> {
    return this.http.delete<APIResponse<{ success: boolean; message?: string }>>(
      environment.BASE_URL_API + 'inbox/settings/mailboxes/' + id
    );
  }

  // Field Visibility
  public getFieldVisibilityCatalog(): Observable<APIResponse<IFieldVisibilityCatalog>> {
    return this.http.get<APIResponse<IFieldVisibilityCatalog>>(
      environment.BASE_URL_API + 'field-visibility/catalog'
    );
  }

  public getRoleHiddenFields(roleId: number): Observable<APIResponse<HiddenFieldsByModule>> {
    return this.http.get<APIResponse<HiddenFieldsByModule>>(
      environment.BASE_URL_API + 'roles/' + roleId + '/hidden-fields'
    );
  }

  public bulkUpdateRoleHiddenFields(
    roleId: number,
    module: FieldVisibilityModule,
    action: 'hide_all' | 'show_all',
  ): Observable<APIResponse<HiddenFieldsByModule>> {
    return this.http.post<APIResponse<HiddenFieldsByModule>>(
      environment.BASE_URL_API + 'roles/' + roleId + '/hidden-fields/bulk',
      { module, action }
    );
  }

  // Orders Settings
  public getOrdersSettings(): Observable<APIResponse<IOrdersSettings>> {
    return this.http.get<APIResponse<IOrdersSettings>>(environment.BASE_URL_API + 'settings/orders');
  }

  public updateOrdersSettings(settings: IOrdersSettings): Observable<APIResponse<IOrdersSettings>> {
    return this.http.patch<APIResponse<IOrdersSettings>>(environment.BASE_URL_API + 'settings/orders/update', settings);
  }

  // Smart Replies settings (WB-6907) — /v1/settings/smart-replies
  public getSmartRepliesSettings(): Observable<ISmartRepliesSettings> {
    return this.http
      .get<APIResponse<ISmartRepliesSettings>>(environment.BASE_URL_API + 'settings/smart-replies')
      .pipe(map((res) => this.unwrapSmartReplies(res)));
  }

  public updateSmartRepliesSettings(settings: ISmartRepliesSettings): Observable<ISmartRepliesSettings> {
    return this.http
      .patch<APIResponse<ISmartRepliesSettings>>(environment.BASE_URL_API + 'settings/smart-replies/update', settings)
      .pipe(map((res) => this.unwrapSmartReplies(res)));
  }

  // Odoo may return the payload under `object` or `data[0]` — tolerate both so a
  // mismatched envelope doesn't hand the component an undefined settings object.
  private unwrapSmartReplies(res: APIResponse<ISmartRepliesSettings>): ISmartRepliesSettings {
    return (res?.object ?? res?.data?.[0]) as ISmartRepliesSettings;
  }

  // Splitting Rules
  public listSplittingRules(): Observable<any> {
    return this.http.get<any>(environment.BASE_URL_API + 'segregation/mechanisms?include_inactive=true');
  }

  public createSplittingRule(rule: ISplittingRuleCreate): Observable<any> {
    return this.http.post<any>(environment.BASE_URL_API + 'segregation/mechanisms', rule);
  }

  public updateSplittingRule(id: number, rule: Partial<ISplittingRuleCreate>): Observable<any> {
    return this.http.put<any>(environment.BASE_URL_API + 'segregation/mechanisms/' + id, rule);
  }

  public deleteSplittingRule(id: number): Observable<any> {
    return this.http.delete<any>(environment.BASE_URL_API + 'segregation/mechanisms/' + id);
  }

  public listCustomFieldValues(fieldId: number): Observable<any> {
    return this.http.get<any>(environment.BASE_URL_API + 'segregation/custom-field-values/' + fieldId);
  }

  public listApplicabilityFields(): Observable<any> {
    return this.http.get<any>(environment.BASE_URL_API + 'segregation/applicability-fields');
  }

  public listCustomerGroups(q?: string): Observable<any> {
    const qs = q ? `?q=${encodeURIComponent(q)}` : '';
    return this.http.get<any>(environment.BASE_URL_API + 'segregation/customer-groups' + qs);
  }

  public listSplitLogs(limit = 50, offset = 0): Observable<any> {
    return this.http.get<any>(
      `${environment.BASE_URL_API}segregation/logs?limit=${limit}&offset=${offset}`
    );
  }
}